/* eslint-disable no-console */
import type { FilterSourceType, ComplianceModeType } from 'src/pages/Dashboard/store';

const STORAGE_KEY = 'minerva_dashboard_prefs';
const PERSISTENCE_ENABLED = true;

// Configuration for which fields should be persisted
const PERSIST_FIELDS = {
  filterSource: false,
  selectedGeneratorTypesEAR: false,
  selectedGeneratorTypesKPI: false,
  selectedPolarChartSector: false,
  complianceMode: true,
};

const VALID_FILTER_SOURCES: readonly FilterSourceType[] = ['ear', 'kpi', 'topCards', null];
const VALID_COMPLIANCE_MODES: readonly ComplianceModeType[] = ['notCompliant', 'compliant', 'not-compliant-by-container'];

export interface DashboardPersistedState {
  filterSource?: FilterSourceType;
  selectedGeneratorTypesEAR?: string[];
  selectedGeneratorTypesKPI?: string[];
  selectedPolarChartSector?: string | null;
  complianceMode?: ComplianceModeType;
}

const isValidFilterSource = (
  value: unknown,
): value is FilterSourceType => {
  return VALID_FILTER_SOURCES.includes(value as FilterSourceType);
};

const isValidGeneratorTypes = (value: unknown): value is string[] => {
  return (
    Array.isArray(value) && value.every((item) => typeof item === 'string')
  );
};

const isValidSectorId = (value: unknown): value is string | null => {
  return value === null || typeof value === 'string';
};

const isValidComplianceMode = (
  value: unknown,
): value is ComplianceModeType | undefined => {
  return value === undefined || VALID_COMPLIANCE_MODES.includes(value as ComplianceModeType);
};

const validateState = (data: unknown): DashboardPersistedState | null => {
  if (typeof data !== 'object' || data === null) {
    return null;
  }

  const obj = data as Record<string, unknown>;
  const result: DashboardPersistedState = {};

  // Only validate and include fields that are enabled for persistence
  if (PERSIST_FIELDS.filterSource) {
    if (!isValidFilterSource(obj.filterSource)) {
      return null;
    }
    result.filterSource = obj.filterSource;
  }

  if (PERSIST_FIELDS.selectedGeneratorTypesEAR) {
    if (!isValidGeneratorTypes(obj.selectedGeneratorTypesEAR)) {
      return null;
    }
    result.selectedGeneratorTypesEAR = obj.selectedGeneratorTypesEAR;
  }

  if (PERSIST_FIELDS.selectedGeneratorTypesKPI) {
    if (!isValidGeneratorTypes(obj.selectedGeneratorTypesKPI)) {
      return null;
    }
    result.selectedGeneratorTypesKPI = obj.selectedGeneratorTypesKPI;
  }

  if (PERSIST_FIELDS.selectedPolarChartSector) {
    result.selectedPolarChartSector = isValidSectorId(obj.selectedPolarChartSector)
      ? obj.selectedPolarChartSector
      : null;
  }

  if (PERSIST_FIELDS.complianceMode) {
    result.complianceMode = isValidComplianceMode(obj.complianceMode)
      ? obj.complianceMode
      : 'compliant';
  }

  return result;
};

const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

export const DashboardPersistenceManager = {
  loadState(): DashboardPersistedState | null {
    if (!PERSISTENCE_ENABLED) {
      if (isLocalStorageAvailable()) {
        try {
          localStorage.removeItem(STORAGE_KEY);
        } catch {
        }
      }
      return null;
    }

    if (!isLocalStorageAvailable()) {
      console.warn('localStorage unavailable for dashboard persistence');
      return null;
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY);

      if (stored === null) {
        return null;
      }

      const parsed = JSON.parse(stored);
      const validated = validateState(parsed);

      if (validated === null) {
        console.warn(
          'Dashboard persistence data corrupted, resetting to defaults',
        );
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      return validated;
    } catch {
      console.warn(
        'Dashboard persistence data corrupted, resetting to defaults',
      );
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
  },

  saveState(state: DashboardPersistedState): void {
    if (!PERSISTENCE_ENABLED) {
      return;
    }

    if (!isLocalStorageAvailable()) {
      console.warn('localStorage unavailable for dashboard persistence');
      return;
    }

    try {
      // Only save fields that are enabled for persistence
      const filteredState: DashboardPersistedState = {};
      if (PERSIST_FIELDS.filterSource && state.filterSource !== undefined) {
        filteredState.filterSource = state.filterSource;
      }
      if (PERSIST_FIELDS.selectedGeneratorTypesEAR && state.selectedGeneratorTypesEAR !== undefined) {
        filteredState.selectedGeneratorTypesEAR = state.selectedGeneratorTypesEAR;
      }
      if (PERSIST_FIELDS.selectedGeneratorTypesKPI && state.selectedGeneratorTypesKPI !== undefined) {
        filteredState.selectedGeneratorTypesKPI = state.selectedGeneratorTypesKPI;
      }
      if (PERSIST_FIELDS.selectedPolarChartSector && state.selectedPolarChartSector !== undefined) {
        filteredState.selectedPolarChartSector = state.selectedPolarChartSector;
      }
      if (PERSIST_FIELDS.complianceMode && state.complianceMode !== undefined) {
        filteredState.complianceMode = state.complianceMode;
      }

      const serialized = JSON.stringify(filteredState);
      localStorage.setItem(STORAGE_KEY, serialized);
    } catch (error) {
      if (
        error instanceof Error &&
        (error.name === 'QuotaExceededError' ||
          error.message.includes('quota'))
      ) {
        console.warn(
          'localStorage quota exceeded, skipping dashboard persistence save',
        );
      } else {
        console.warn('localStorage unavailable for dashboard persistence');
      }
    }
  },

  clearState(): void {
    if (!isLocalStorageAvailable()) {
      return;
    }

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
    }
  },
};